import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTTable from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTPageLayout from '../common/RTPageLayout';
import { apiGet } from '../../api/apiClient';
import '../common/rtCommon.css';
import './ViewBuildings.css';
import { toastError } from '../common/toast';
import {
  formatOccupancyType,
  BUILDING_OCCUPANCY_TYPE_FILTER_OPTIONS,
} from '../../utils/occupancyConstants';

const getDefaultFilters = () => ({
  building_id: undefined,
  occupancy_gender: undefined,
});

const ViewBuildings = () => {
  const [buildings, setBuildings] = useState([]);
  const [summary, setSummary] = useState({
    total_beds: 0,
    occupied_beds: 0,
    vacant_beds: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters);
  const navigate = useNavigate();
  const location = useLocation();

  const filterFields = useMemo(
    () => [
      {
        key: 'building_id',
        label: 'Building',
        type: 'autocomplete',
        width: 'medium',
        placeholder: 'Search building...',
        options: buildingOptions,
        applyImmediately: true,
      },
      {
        key: 'occupancy_gender',
        label: 'Type',
        type: 'dropdown',
        width: 'compact',
        options: BUILDING_OCCUPANCY_TYPE_FILTER_OPTIONS,
        applyImmediately: true,
      },
    ],
    [buildingOptions],
  );

  useEffect(() => {
    const init = async () => {
      try {
        const res = await apiGet('/building/list');
        const list = res.data?.buildings || res.data || [];
        setBuildingOptions(
          list.map((b) => ({
            value: String(b.id),
            label: b.name,
          })),
        );
      } catch (err) {
        console.error('Error loading building options:', err);
      }
      fetchBuildings(getDefaultFilters());
    };
    init();
  }, []);

  const fetchBuildings = async (filterParams = filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterParams.building_id) {
        params.append('building_id', filterParams.building_id);
      }
      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }
      const query = params.toString();
      const url = query ? `/building/list?${query}` : '/building/list';
      const res = await apiGet(url);
      setBuildings(res.data?.buildings || res.data || []);
      setSummary(
        res.data?.summary || {
          total_beds: 0,
          occupied_beds: 0,
          vacant_beds: 0,
        },
      );
    } catch (err) {
      setError('Failed to load buildings. Please try again.');
      toastError('Error fetching buildings.');
    } finally {
      setLoading(false);
    }
  };

  const updateFiltersAndFetch = (updates) => {
    const next = { ...filters, ...updates };
    setFilters(next);
    fetchBuildings(next);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'building_id' && (value === undefined || value === '')) {
      setBuildingSearch('');
    }
    updateFiltersAndFetch({ [key]: value || undefined });
  };

  const handleFilterSearchChange = (key, text) => {
    if (key === 'building_id') {
      setBuildingSearch(text);
      if (!text) {
        updateFiltersAndFetch({ building_id: undefined });
      }
    }
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (filters.building_id) {
      const buildingLabel =
        buildingSearch ||
        buildingOptions.find((o) => o.value === String(filters.building_id))
          ?.label ||
        filters.building_id;
      pills.push({ key: 'building_id', label: `Building: ${buildingLabel}` });
    }
    if (filters.occupancy_gender) {
      const typeLabel =
        BUILDING_OCCUPANCY_TYPE_FILTER_OPTIONS.find(
          (o) => o.value === filters.occupancy_gender,
        )?.label || filters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Type: ${typeLabel}` });
    }
    return pills;
  }, [filters, buildingSearch, buildingOptions]);

  const handleRemoveAppliedFilter = (key) => {
    if (key === 'building_id') {
      setBuildingSearch('');
      updateFiltersAndFetch({ building_id: undefined });
    } else if (key === 'occupancy_gender') {
      updateFiltersAndFetch({ occupancy_gender: undefined });
    }
  };

  const handleClearAllFilters = () => {
    setBuildingSearch('');
    const next = getDefaultFilters();
    setFilters(next);
    fetchBuildings(next);
  };

  const handleViewBuilding = (buildingId) => {
    navigate(`/building/view/${buildingId}`);
  };

  const columns = [
    { Header: 'Building Name', accessor: 'name' },
    {
      Header: 'Type',
      accessor: 'occupancy_gender',
      Cell: ({ value }) => formatOccupancyType(value),
    },
    { Header: 'Address', accessor: 'address' },
    {
      Header: 'Rooms',
      accessor: 'room_count',
      Cell: (cell) => cell.value || 0,
    },
    {
      Header: 'Beds (Occupied/Total)',
      accessor: 'bed_occupancy',
      Cell: ({ row }) => {
        const occupied = row.occupied_beds || 0;
        const total = row.total_beds || 0;
        return `${occupied} / ${total}`;
      },
    },
    {
      Header: 'Vacant Beds',
      accessor: 'vacant_beds',
      Cell: (cell) => cell.value || 0,
    },
    {
      Header: 'Ownership',
      accessor: 'type',
      Cell: ({ value }) =>
        value ? value.charAt(0).toUpperCase() + value.slice(1) : '-',
    },
    {
      Header: 'Rent',
      accessor: 'rent',
      Cell: (cell) => `\u20B9 ${cell.value}`,
    },
    { Header: 'Status', accessor: 'status' },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: (cell) => (
        <RTButton type="link" onClick={() => handleViewBuilding(cell.value)}>
          View
        </RTButton>
      ),
    },
  ];

  return (
    <RTPageLayout
      title="View Buildings"
      actions={
        <RTButton variant="secondary" onClick={() => navigate(-1)}>
          &#8592; Back
        </RTButton>
      }
      mainSection={true}
      activePath={location.pathname}
    >
      {error ? (
        <div className="rt-error-container">
          <div className="rt-error-message">{error}</div>
          <RTButton
            variant="primary"
            onClick={() => fetchBuildings(filters)}
            style={{ marginTop: '20px' }}
          >
            Retry
          </RTButton>
        </div>
      ) : (
        <RTFilteredTable
          loading={loading}
          loadingMessage="Loading buildings..."
          filters={
            <RTFilters
              fields={filterFields}
              values={filters}
              searchValues={{ building_id: buildingSearch }}
              onChange={handleFilterChange}
              onSearchChange={handleFilterSearchChange}
              pills={appliedFilterPills}
              onRemovePill={handleRemoveAppliedFilter}
              onClearAll={handleClearAllFilters}
            />
          }
        >
          <RTTable
            columns={columns}
            data={buildings}
            title={
              <>
                <span className="building-summary-inline">
                  Total Beds: <strong>{summary.total_beds}</strong>
                  &nbsp;&nbsp;Occupied: <strong>{summary.occupied_beds}</strong>
                  &nbsp;&nbsp;Vacant: <strong>{summary.vacant_beds}</strong>
                </span>
              </>
            }
            showPagination={true}
            showSearch={true}
            exportable={true}
            exportFileName="Buildings"
            noDataText="No buildings found."
          />
        </RTFilteredTable>
      )}
    </RTPageLayout>
  );
};

export default ViewBuildings;
